/**
 * Image Alignment Overlay for AR
 *
 * Shows a semi-transparent pose-locked preview of the target image during XR,
 * allowing the user to align it with the physical printed image before placing
 * the scene. Works on both mobile AR (screen taps) and immersive headsets.
 *
 * All UI is pure Babylon.js 3D — no DOM elements.
 *
 * Meshes live in world space with billboard mode for facing. A per-frame
 * observer repositions the root to stay in front of the camera.
 * The XR camera's world matrix cannot be decomposed reliably (returns identity),
 * so we use globalPosition + getForwardRay() for positioning instead.
 */

import { Logger } from "../utils/Logger";
import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import "@babylonjs/core/Meshes/Builders/planeBuilder"; // Side effect for CreatePlane
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Constants } from "@babylonjs/core/Engines/constants";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import { Observer } from "@babylonjs/core/Misc/observable";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { Ellipse } from "@babylonjs/gui/2D/controls/ellipse";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { Control } from "@babylonjs/gui/2D/controls/control";

import type { WebXRCamera } from "@babylonjs/core/XR/webXRCamera";

/** Result returned when the user confirms alignment. */
export interface AlignmentResult {
    /** World position of the image center. */
    position: Vector3;
    /** World orientation of the image plane (facing the user). */
    rotationQuaternion: Quaternion;
}

export interface ImageAlignmentConfig {
    /** Full URL to the target image. */
    imageUrl: string;
    /** Physical width of the image in meters. */
    imageWidthMeters: number;
    /** Babylon scene. */
    scene: Scene;
    /** The active XR camera. */
    xrCamera: WebXRCamera;
    /** When true, image tracking is running in parallel — show a scanning indicator. */
    autoTracking?: boolean;
}

// -- Default sizing for mobile AR (narrow FOV phones) --
const MOBILE_DISPLAY_DISTANCE = 0.6;
const MOBILE_SCREEN_FRACTION = 0.7;

// -- Sizing for headsets (wide FOV, e.g. Quest at ~100°) --
// The preview must subtend the same angle as the physical marker at
// a comfortable arm's-length distance (~0.5m). With a smaller screen
// fraction the computed markerDistance = imageWidthMeters * distance / displayWidth
// lands at a realistic range, so the instantiated scene matches in size.
const HEADSET_DISPLAY_DISTANCE = 0.8;
const HEADSET_SCREEN_FRACTION = 0.15;

/**
 * Projection matrix scale threshold to distinguish headsets from phones.
 * max(M[0][0], M[1][1]) encodes 1 / (aspect * tan(fov/2)).
 * Phones (narrow FOV ~60°) have maxScale > 2.0; headsets (~100°) are ~1.0–1.3.
 */
const HEADSET_MAX_SCALE_THRESHOLD = 1.5;

/** Padding around the image for the white border, in meters. */
const BORDER_PADDING = 0.005;

// Register inline shaders for the image overlay. A custom ShaderMaterial
// is needed because StandardMaterial with textures does not reliably write
// the correct framebuffer alpha for WebXR AR compositing.
const SHADER_NAME = '__alignmentImage';
Effect.ShadersStore[`${SHADER_NAME}VertexShader`] = /* glsl */`
    precision highp float;
    attribute vec3 position;
    attribute vec2 uv;
    uniform mat4 worldViewProjection;
    varying vec2 vUV;
    void main() {
        vUV = vec2(uv.x, 1.0 - uv.y);
        gl_Position = worldViewProjection * vec4(position, 1.0);
    }
`;
Effect.ShadersStore[`${SHADER_NAME}FragmentShader`] = /* glsl */`
    precision highp float;
    uniform sampler2D textureSampler;
    uniform float alpha;
    uniform float useTexture;
    uniform vec3 baseColor;
    varying vec2 vUV;
    void main() {
        vec3 rgb = baseColor;
        float srcA = alpha;
        if (useTexture > 0.5) {
            vec4 texel = texture2D(textureSampler, vUV);
            rgb = texel.rgb;
            srcA = texel.a * alpha;
        }
        // Premultiplied alpha output for Porter-Duff compositing.
        // The XR compositor uses framebuffer alpha to blend the camera feed,
        // so we must output correct premultiplied values.
        gl_FragColor = vec4(rgb * srcA, srcA);
    }
`;

/**
 * 3D alignment overlay that follows the XR camera each frame.
 * Meshes are in world space with billboard mode; a per-frame observer
 * repositions the root along the camera's forward ray.
 */
export class ImageAlignmentOverlay {
    private _scene: Scene;
    private _xrCamera: WebXRCamera;
    private _imageUrl: string;
    private _imageWidthMeters: number;
    private _autoTracking: boolean;

    private _root: TransformNode | null = null;
    private _displayDistance = MOBILE_DISPLAY_DISTANCE;
    private _targetScreenFraction = MOBILE_SCREEN_FRACTION;
    private _displayWidth = 0;
    /** World-space vertical offset to compensate for asymmetric projection (Quest). */
    private _projectionVOffset = 0;
    private _imagePlane: Mesh | null = null;
    private _imageMaterial: ShaderMaterial | null = null;
    private _borderPlane: Mesh | null = null;
    private _borderMaterial: ShaderMaterial | null = null;
    private _instructionPlane: Mesh | null = null;
    private _instructionTexture: AdvancedDynamicTexture | null = null;
    private _buttonPlane: Mesh | null = null;
    private _buttonTexture: AdvancedDynamicTexture | null = null;
    private _pointerObserver: Observer<import("@babylonjs/core/Events/pointerEvents").PointerInfo> | null = null;
    /** Reusable scratch vector to avoid per-frame allocations on Quest. */
    private readonly _scratchCamUp = new Vector3();
    private _renderObserver: Observer<Scene> | null = null;
    private _projWaitObserver: Observer<Scene> | null = null;
    private _instructionAnimObserver: Observer<Scene> | null = null;

    /** Callback invoked when the user confirms alignment. */
    private _onConfirmCallback: ((result: AlignmentResult) => void) | null = null;

    constructor(config: ImageAlignmentConfig) {
        this._scene = config.scene;
        this._xrCamera = config.xrCamera;
        this._imageUrl = config.imageUrl;
        this._imageWidthMeters = config.imageWidthMeters;
        this._autoTracking = config.autoTracking ?? false;
    }

    /** Register a one-shot callback for when the user confirms alignment. */
    onConfirm(callback: (result: AlignmentResult) => void): void {
        this._onConfirmCallback = callback;
    }

    /** Create meshes and start following the XR camera. */
    show(): void {
        // The XR camera's projection matrix may not be populated on the first
        // frame. Wait until it has valid values before creating the overlay,
        // so _computeDisplayWidth() can derive the correct size.
        const proj = this._xrCamera.getProjectionMatrix();
        if (Math.abs(proj.m[0]) > 1e-6 && Math.abs(proj.m[5]) > 1e-6) {
            this._createOverlay();
            this._startFollowingCamera();
        } else {
            Logger.log("[ImageAlignment] Projection matrix not ready, deferring overlay creation");
            let waitFrames = 0;
            // Use onAfterRender — the XR camera projection matrix is populated
            // from the XRView during the render pass, so it's only valid *after*
            // render, not before.
            const waitObserver = this._scene.onAfterRenderObservable.add(() => {
                if (++waitFrames > 120) {
                    this._scene.onAfterRenderObservable.remove(waitObserver);
                    this._projWaitObserver = null;
                    Logger.warn("[ImageAlignment] Projection matrix not ready after 120 frames, skipping overlay");
                    return;
                }
                const p = this._xrCamera.getProjectionMatrix();
                if (Math.abs(p.m[0]) > 1e-6 && Math.abs(p.m[5]) > 1e-6) {
                    this._scene.onAfterRenderObservable.remove(waitObserver);
                    this._projWaitObserver = null;
                    this._createOverlay();
                    this._startFollowingCamera();
                }
            });
            // Store so dispose() can clean up if called while waiting
            this._projWaitObserver = waitObserver;
        }
    }

    /** Dispose all resources. */
    dispose(): void {
        if (this._projWaitObserver) {
            this._scene.onAfterRenderObservable.remove(this._projWaitObserver);
            this._projWaitObserver = null;
        }

        if (this._renderObserver) {
            this._scene.onBeforeRenderObservable.remove(this._renderObserver);
            this._renderObserver = null;
        }

        if (this._instructionAnimObserver) {
            this._scene.onBeforeRenderObservable.remove(this._instructionAnimObserver);
            this._instructionAnimObserver = null;
        }

        if (this._pointerObserver) {
            this._scene.onPointerObservable.remove(this._pointerObserver);
            this._pointerObserver = null;
        }

        this._instructionTexture?.dispose();
        this._instructionTexture = null;
        this._buttonTexture?.dispose();
        this._buttonTexture = null;

        this._imageMaterial?.dispose();
        this._imageMaterial = null;
        this._borderMaterial?.dispose();
        this._borderMaterial = null;

        this._imagePlane?.dispose();
        this._imagePlane = null;
        this._borderPlane?.dispose();
        this._borderPlane = null;
        this._instructionPlane?.dispose();
        this._instructionPlane = null;
        this._buttonPlane?.dispose();
        this._buttonPlane = null;

        this._root?.dispose();
        this._root = null;

        this._onConfirmCallback = null;
    }

    // ---- Internal ----

    /**
     * Compute the world-space width that makes the image appear as
     * TARGET_SCREEN_FRACTION of the smaller screen dimension.
     *
     * The projection matrix encodes the camera's field of view and aspect ratio:
     *   M[0][0] = 1 / (aspect * tan(fov_y/2))   — horizontal scale
     *   M[1][1] = 1 / tan(fov_y/2)               — vertical scale
     *
     * A world-space width `w` at distance `d` occupies NDC width = w * M[0][0] / d.
     * NDC spans [-1, 1] (total = 2), so screen fraction = w * M[0][0] / (2 * d).
     *
     * The smaller screen dimension has the *larger* projection factor
     * (max of M[0][0], M[1][1]), so we use that to target it.
     *
     * Solving for w:  w = fraction * 2 * d / max(M[0][0], M[1][1])
     */
    /** Requires a valid projection matrix — show() ensures this before calling. */
    private _computeDisplayWidth(): number {
        const proj = this._xrCamera.getProjectionMatrix();
        // m[0] = M[0][0], m[5] = M[1][1] in column-major layout
        const maxScale = Math.max(Math.abs(proj.m[0]), Math.abs(proj.m[5]));

        // Detect headset vs mobile from projection matrix.
        // Wide FOV headsets (Quest ~100°) have small scale factors (~1.0–1.3);
        // phones (~60°) have larger factors (>2.0).
        if (maxScale < HEADSET_MAX_SCALE_THRESHOLD) {
            this._displayDistance = HEADSET_DISPLAY_DISTANCE;
            this._targetScreenFraction = HEADSET_SCREEN_FRACTION;
            Logger.log(`[ImageAlignment] Detected headset (maxScale=${maxScale.toFixed(2)}), using distance=${this._displayDistance}m, fraction=${this._targetScreenFraction}`);
        } else {
            this._displayDistance = MOBILE_DISPLAY_DISTANCE;
            this._targetScreenFraction = MOBILE_SCREEN_FRACTION;
            Logger.log(`[ImageAlignment] Detected mobile (maxScale=${maxScale.toFixed(2)}), using distance=${this._displayDistance}m, fraction=${this._targetScreenFraction}`);
        }

        // Quest headsets have an asymmetric vertical FOV (more below than above).
        // getForwardRay() returns the geometric center (view-matrix only), so
        // content placed on it appears above the visual center of the display.
        // m[9] encodes the vertical frustum offset; compensate by shifting the
        // overlay along camera up by: offset = d * m[9] / m[5].
        // In Babylon's LH convention, m[9] is negative on Quest, producing a
        // downward shift that moves content toward the visual center.
        // Quest 2: ~1° shift, Quest 3: ~5° shift.
        const m5 = proj.m[5];
        if (Math.abs(m5) > 1e-6) {
            this._projectionVOffset = this._displayDistance * proj.m[9] / m5;
        }
        Logger.log(
            `[ImageAlignment] Projection: m[0]=${proj.m[0].toFixed(4)}, m[5]=${m5.toFixed(4)}, ` +
            `m[8]=${proj.m[8].toFixed(4)}, m[9]=${proj.m[9].toFixed(4)}, ` +
            `vCompensation=${this._projectionVOffset.toFixed(4)}m`
        );

        return this._targetScreenFraction * 2 * this._displayDistance / maxScale;
    }

    private _createOverlay(): void {
        const scene = this._scene;

        // Root node in world space — position updated each frame.
        // Billboard mode on the root makes ALL children face the camera as
        // a single unit, preventing individual planes from floating apart.
        const root = new TransformNode("alignment-root", scene);
        root.billboardMode = TransformNode.BILLBOARDMODE_ALL;
        this._root = root;

        // Display width — sized to fill ~half the smaller screen dimension.
        // Also sets _projectionVOffset for asymmetric-projection compensation.
        const displayWidth = this._computeDisplayWidth();
        this._displayWidth = displayWidth;

        // Image preview plane — NO individual billboard (root handles facing)
        const imagePlane = MeshBuilder.CreatePlane("alignment-image", { width: displayWidth, height: displayWidth }, scene);
        imagePlane.parent = root;
        imagePlane.isPickable = false;
        this._imagePlane = imagePlane;

        // Custom ShaderMaterial with premultiplied Porter-Duff alpha for correct
        // AR compositing. StandardMaterial's ALPHA_COMBINE uses (ONE, ONE) for the
        // alpha channel, which drives framebuffer alpha toward 1.0 — making the XR
        // compositor treat pixels as fully opaque. Porter-Duff uses
        // (ONE, ONE_MINUS_SRC_ALPHA) for both color and alpha channels.
        const imageMat = new ShaderMaterial("alignment-image-mat", scene, SHADER_NAME, {
            attributes: ['position', 'uv'],
            uniforms: ['worldViewProjection', 'alpha', 'useTexture', 'baseColor'],
            samplers: ['textureSampler'],
            needAlphaBlending: true,
        });
        imageMat.backFaceCulling = false;
        imageMat.alphaMode = Constants.ALPHA_PREMULTIPLIED_PORTERDUFF;
        imageMat.setFloat('alpha', 0.5);
        imageMat.setFloat('useTexture', 1.0);
        imageMat.setVector3('baseColor', Vector3.Zero());
        this._imageMaterial = imageMat;

        const texture = new Texture(this._imageUrl, scene, false, false, Texture.BILINEAR_SAMPLINGMODE);

        texture.onLoadObservable.addOnce(() => {
            const size = texture.getSize();

            imageMat.setTexture('textureSampler', texture);

            // Assign material to the plane only after texture is ready
            imagePlane.material = imageMat;

            if (size.width > 0 && size.height > 0) {
                const aspect = size.width / size.height;
                const h = displayWidth / aspect;
                imagePlane.scaling.y = h / displayWidth;

                this._repositionUI(displayWidth, h);

                Logger.log(`[ImageAlignment] Texture loaded: ${size.width}x${size.height}, aspect=${aspect.toFixed(2)}, displaySize=${displayWidth.toFixed(3)}x${h.toFixed(3)}m`);
            }
        });

        // Thin white border behind the image
        const borderPlane = MeshBuilder.CreatePlane("alignment-border", {
            width: displayWidth + BORDER_PADDING * 2,
            height: displayWidth + BORDER_PADDING * 2,
        }, scene);
        borderPlane.parent = root;
        borderPlane.position.z = 0.001;
        borderPlane.isPickable = false;
        // Render border in group 0 (default), image in group 1 — guarantees
        // the border draws first so the image composites on top consistently.
        borderPlane.renderingGroupId = 0;
        imagePlane.renderingGroupId = 1;
        this._borderPlane = borderPlane;

        const borderMat = new ShaderMaterial("alignment-border-mat", scene, SHADER_NAME, {
            attributes: ['position', 'uv'],
            uniforms: ['worldViewProjection', 'alpha', 'useTexture', 'baseColor'],
            samplers: ['textureSampler'],
            needAlphaBlending: true,
        });
        borderMat.backFaceCulling = false;
        borderMat.alphaMode = Constants.ALPHA_PREMULTIPLIED_PORTERDUFF;
        borderMat.setFloat('alpha', 0.5);
        borderMat.setFloat('useTexture', 0.0);
        borderMat.setVector3('baseColor', new Vector3(1, 1, 1));
        borderPlane.material = borderMat;
        this._borderMaterial = borderMat;

        // Instruction text above image
        this._createInstructionText(displayWidth);

        // Play button below image
        this._createPlayButton(displayWidth);

        Logger.log(`[ImageAlignment] Overlay created (imageWidth=${this._imageWidthMeters}m, displayWidth=${displayWidth.toFixed(3)}m, distance=${this._displayDistance}m, url=${this._imageUrl})`);
    }

    private _createInstructionText(imageWidth: number): void {
        if (!this._root) return;
        const scene = this._scene;
        const textWidth = Math.max(imageWidth, 0.2);
        const textHeight = 0.03;

        const textPlane = MeshBuilder.CreatePlane("alignment-instruction", { width: textWidth, height: textHeight }, scene);
        textPlane.parent = this._root;
        // Initial position assuming square image; _repositionUI corrects
        // once the real aspect ratio is known.
        textPlane.position.y = imageWidth / 2 + 0.04;
        textPlane.isPickable = false;
        this._instructionPlane = textPlane;

        const adt = AdvancedDynamicTexture.CreateForMesh(textPlane, 512, 64, false);
        adt.hasAlpha = true;
        this._instructionTexture = adt;

        const baseText = this._autoTracking ? "Scanning for image" : "Align with printed image";
        const textBlock = new TextBlock("instruction-text", baseText);
        textBlock.color = "white";
        textBlock.fontSize = 36;
        textBlock.fontFamily = "sans-serif";
        textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        textBlock.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        adt.addControl(textBlock);

        // Animate ellipsis when auto-tracking is active to indicate scanning
        if (this._autoTracking) {
            let dotCount = 0;
            this._instructionAnimObserver = scene.onBeforeRenderObservable.add(() => {
                // Cycle dots every ~500ms (assuming ~60fps, every 30 frames)
                const frame = Math.floor(performance.now() / 500);
                const newCount = frame % 4;
                if (newCount !== dotCount) {
                    dotCount = newCount;
                    textBlock.text = baseText + ".".repeat(dotCount);
                }
            });
        }
    }

    private _createPlayButton(imageWidth: number): void {
        if (!this._root) return;
        const scene = this._scene;
        const buttonSize = 0.06;

        const buttonPlane = MeshBuilder.CreatePlane("alignment-play-button", { width: buttonSize, height: buttonSize }, scene);
        buttonPlane.parent = this._root;
        buttonPlane.position.y = -(imageWidth / 2 + 0.04);
        buttonPlane.isPickable = true;
        this._buttonPlane = buttonPlane;

        const adt = AdvancedDynamicTexture.CreateForMesh(buttonPlane, 128, 128, false);
        adt.hasAlpha = true;
        this._buttonTexture = adt;

        const circle = new Ellipse("play-circle");
        circle.width = "100px";
        circle.height = "100px";
        circle.thickness = 3;
        circle.color = "white";
        circle.background = "rgba(0, 180, 80, 0.8)";
        adt.addControl(circle);

        const playIcon = new TextBlock("play-icon", "\u25B6");
        playIcon.color = "white";
        playIcon.fontSize = 48;
        playIcon.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        playIcon.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        playIcon.paddingLeft = "6px";
        circle.addControl(playIcon);

        this._pointerObserver = scene.onPointerObservable.add((pointerInfo) => {
            if (pointerInfo.type === PointerEventTypes.POINTERTAP) {
                if (pointerInfo.pickInfo?.pickedMesh === buttonPlane) {
                    this._confirmAlignment();
                }
            }
        });
    }

    /** Reposition instruction text and play button once image aspect ratio is known. */
    private _repositionUI(_imageWidth: number, imageHeight: number): void {
        // Use the same gap for text and button so the rig is symmetric around
        // the image. The image stays at y=0 so placement math is unaffected.
        const gap = 0.04;
        if (this._instructionPlane) {
            this._instructionPlane.position.y = imageHeight / 2 + gap;
        }
        if (this._buttonPlane) {
            this._buttonPlane.position.y = -(imageHeight / 2 + gap);
        }
        if (this._borderPlane) {
            this._borderPlane.scaling.y = (imageHeight + BORDER_PADDING * 2) / (this._displayWidth + BORDER_PADDING * 2);
        }
    }

    /**
     * Each frame, move the root DISPLAY_DISTANCE meters along the camera's
     * full forward direction (not just XZ-projected). This keeps the overlay
     * in front of the camera even when tilting up/down.
     * Billboard mode handles the facing.
     */
    private _startFollowingCamera(): void {
        this._updatePosition();

        this._renderObserver = this._scene.onBeforeRenderObservable.add(() => {
            this._updatePosition();
        });
    }

    private _updatePosition(): void {
        if (!this._root) return;

        const camPos = this._xrCamera.globalPosition;
        const forward = this._xrCamera.getForwardRay().direction;

        // Place root along the full forward direction (follows tilt).
        // Shift along camera up to compensate for asymmetric projection —
        // without this, content appears above the visual center on Quest
        // because getForwardRay() returns the geometric center, not the
        // optical center of the asymmetric frustum.
        const d = this._displayDistance;
        const v = this._projectionVOffset;

        if (Math.abs(v) < 1e-6) {
            // Symmetric projection (mobile) — no compensation needed
            this._root.position.set(
                camPos.x + forward.x * d,
                camPos.y + forward.y * d,
                camPos.z + forward.z * d,
            );
        } else {
            Vector3.TransformNormalToRef(Vector3.UpReadOnly, this._xrCamera.getWorldMatrix(), this._scratchCamUp);
            this._scratchCamUp.normalize();
            const camUp = this._scratchCamUp;
            this._root.position.set(
                camPos.x + forward.x * d + camUp.x * v,
                camPos.y + forward.y * d + camUp.y * v,
                camPos.z + forward.z * d + camUp.z * v,
            );
        }
    }

    /** Capture the overlay's world pose and invoke the confirm callback. */
    private _confirmAlignment(): void {
        if (!this._root || !this._onConfirmCallback) return;

        const camPos = this._xrCamera.globalPosition;

        // Build the anchor orientation from the camera's actual pose.
        // The camera's forward ray gives the image normal direction (camera looks
        // at the image), and the camera's up vector defines "top of image".
        // This avoids the degenerate cross-product issue with FromLookDirectionLH
        // when looking straight down (lookDir ≈ parallel to world up).
        const forward = this._xrCamera.getForwardRay().direction;
        const camUp = Vector3.TransformNormal(Vector3.Up(), this._xrCamera.getWorldMatrix());
        camUp.normalize();

        // The overlay is rendered at _displayDistance with displayWidth. When
        // the user aligns it with the physical marker (imageWidthMeters wide),
        // both subtend the same angle. The physical marker's actual distance is:
        //   markerDist = imageWidthMeters * _displayDistance / displayWidth
        const markerDistance = this._imageWidthMeters * this._displayDistance / this._displayWidth;

        // Scale the projection offset from display distance to marker distance
        // so the anchor lands where the image visually appears, not on the raw
        // forward ray (which is offset from the visual center on Quest).
        const vOffset = this._projectionVOffset * (markerDistance / this._displayDistance);
        const anchorPos = new Vector3(
            camPos.x + forward.x * markerDistance + camUp.x * vOffset,
            camPos.y + forward.y * markerDistance + camUp.y * vOffset,
            camPos.z + forward.z * markerDistance + camUp.z * vOffset,
        );

        // Image normal points from image toward camera (opposite of camera forward)
        const imageNormal = forward.negate();

        // Build rotation: -Z = imageNormal (image faces camera), Y = camUp (top of image),
        // X = right. This is an "OpenGL-camera-style" frame; the consumer
        // (`XRManager._applyImageAlignmentPose`) rotates it `Ry(π)` to land
        // the result in the OpenXR anchor convention (+Z = normal).
        const rotationQuat = Quaternion.FromLookDirectionLH(imageNormal, camUp);

        Logger.log(
            `[ImageAlignment] Confirmed: anchorPos=(${anchorPos.x.toFixed(3)}, ${anchorPos.y.toFixed(3)}, ${anchorPos.z.toFixed(3)}), ` +
            `markerDist=${markerDistance.toFixed(3)}m (display=${this._displayDistance}m, displayW=${this._displayWidth.toFixed(3)}m, physW=${this._imageWidthMeters.toFixed(3)}m), ` +
            `camPos=(${camPos.x.toFixed(3)}, ${camPos.y.toFixed(3)}, ${camPos.z.toFixed(3)}), ` +
            `fwd=(${forward.x.toFixed(3)}, ${forward.y.toFixed(3)}, ${forward.z.toFixed(3)}), ` +
            `camUp=(${camUp.x.toFixed(3)}, ${camUp.y.toFixed(3)}, ${camUp.z.toFixed(3)})`
        );

        const callback = this._onConfirmCallback;
        this._onConfirmCallback = null;
        callback({ position: anchorPos, rotationQuaternion: rotationQuat });
    }
}
